import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  GasRoutePhysicoChemicalIndicator,
  GasRoutePhysicoChemicalIndicatorForm,
  GasRoutePhysicoChemicalIndicatorGrid,
} from './types';

export const gasRoutePhysicoChemicalIndicatorApi = createApi({
  reducerPath: 'gasRoutePhysicoChemicalIndicatorApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/gas-route-physico-chemical-indicators`,
  }),
  tagTypes: [
    'GasRoutePhysicoChemicalIndicators',
    'GasRoutePhysicoChemicalIndicator',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getGasRoutePhysicoChemicalIndicatorI: builder.infiniteQuery<
      GasRoutePhysicoChemicalIndicatorGrid[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => [
        'GasRoutePhysicoChemicalIndicators',
        'GasRoutePhysicoChemicalIndicator',
      ],
    }),
    getGasRoutePhysicoChemicalIndicatorById: builder.query<
      GasRoutePhysicoChemicalIndicator,
      string
    >({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: result => [
        { type: 'GasRoutePhysicoChemicalIndicator', id: result?.id },
      ],
    }),
    createGasRoutePhysicoChemicalIndicator: builder.mutation<
      string,
      GasRoutePhysicoChemicalIndicatorForm
    >({
      query: data => ({
        url: ``,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'GasRoutePhysicoChemicalIndicators',
        'GasRoutePhysicoChemicalIndicator',
      ],
    }),
    updateGasRoutePhysicoChemicalIndicatorById: builder.mutation<
      string,
      GasRoutePhysicoChemicalIndicatorForm
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: [
        'GasRoutePhysicoChemicalIndicators',
        'GasRoutePhysicoChemicalIndicator',
      ],
    }),
  }),
});

export const {
  useGetGasRoutePhysicoChemicalIndicatorByIdQuery,
  useCreateGasRoutePhysicoChemicalIndicatorMutation,
  useUpdateGasRoutePhysicoChemicalIndicatorByIdMutation,
} = gasRoutePhysicoChemicalIndicatorApi;

export const useGetGasRoutePhysicoChemicalIndicatorIInfiniteQuery =
  normalizeInfiniteQueryData(
    gasRoutePhysicoChemicalIndicatorApi.useGetGasRoutePhysicoChemicalIndicatorIInfiniteQuery,
  );

const getCachedGasRoutePhysicoChemicalIndicatorByIdSelector = fn =>
  cachedItemByIdSelector(
    'gasRoutePhysicoChemicalIndicatorApi',
    'getGasRoutePhysicoChemicalIndicatorI',
    fn,
  )('');

export const useGetCachedGasRoutePhysicoChemicalIndicatorByIdSelector = fn =>
  useSelector(getCachedGasRoutePhysicoChemicalIndicatorByIdSelector(fn));
